import React, { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { Button } from './ui/button';

const PhotoViewer = ({ photos = [], productName = "Produit retourné" }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!photos || photos.length === 0) return null;

  const openModal = (photoSrc, index) => {
    setSelectedPhoto({ src: photoSrc, index });
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  const nextPhoto = () => {
    if (selectedPhoto && selectedPhoto.index < photos.length - 1) {
      setSelectedPhoto({ 
        src: photos[selectedPhoto.index + 1], 
        index: selectedPhoto.index + 1 
      });
    }
  };

  const prevPhoto = () => {
    if (selectedPhoto && selectedPhoto.index > 0) {
      setSelectedPhoto({ 
        src: photos[selectedPhoto.index - 1], 
        index: selectedPhoto.index - 1 
      });
    }
  };

  return (
    <div className="mt-2">
      {/* Photo Thumbnails */}
      <div className="flex gap-2">
        {photos.slice(0, 4).map((photo, index) => (
          <div key={index} className="relative group cursor-pointer">
            <img
              src={photo}
              alt={`${productName} - Photo ${index + 1}`}
              className="w-16 h-16 object-cover rounded border-2 border-gray-200 hover:border-red-400 transition-all duration-200"
              onClick={() => openModal(photo, index)}
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded flex items-center justify-center">
              <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        ))}
        
        {photos.length > 4 && (
          <div className="w-16 h-16 bg-gray-200 rounded border-2 border-gray-200 flex items-center justify-center text-xs text-gray-600 cursor-pointer hover:bg-gray-300 transition-colors">
            <div className="text-center">
              <span className="block font-semibold">+{photos.length - 4}</span>
              <span className="text-xs">plus</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal for full-size viewing */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close button */}
            <Button
              onClick={closeModal}
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2"
            >
              <X className="h-6 w-6" />
            </Button>

            {/* Navigation buttons */}
            {photos.length > 1 && (
              <>
                {selectedPhoto.index > 0 && (
                  <Button
                    onClick={prevPhoto}
                    variant="ghost"
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </Button>
                )}
                
                {selectedPhoto.index < photos.length - 1 && (
                  <Button
                    onClick={nextPhoto}
                    variant="ghost"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                )}
              </>
            )}

            {/* Main image */}
            <img
              src={selectedPhoto.src}
              alt={`${productName} - Photo ${selectedPhoto.index + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            {/* Photo counter */}
            {photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {selectedPhoto.index + 1} / {photos.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoViewer;